import { useQuery, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';

import toast from '@/components/Toast';
import { documentsApi } from '@/domain/documents/api';
import { documentsKeys } from '@/domain/documents/queries/keys';
import { mergeDeletedPreset, mergeSavedPreset } from '@/domain/documents/queries/gridPresets.merge';
import type { EndpointBody, EndpointQuery, EndpointResponse } from '@/services/api/endpointTypes';
import { showApiErrorToast } from '@/services/api/errorToast';
import { queryOptions } from '@/services/query/queryOptions';
import { useApiMutation } from '@/services/query/useApiMutation';

export type GridPresetsCache = EndpointResponse<typeof documentsApi.getGridPresets>;
type SaveArgs = {
  query: EndpointQuery<typeof documentsApi.createGridPreset>;
  body: EndpointBody<typeof documentsApi.createGridPreset>;
};
type DeleteArgs = {
  query: EndpointQuery<typeof documentsApi.deleteGridPreset>;
  body: EndpointBody<typeof documentsApi.deleteGridPreset>;
};

// A 200 without a `result`: the call did nothing, and `error` may say why. The list and the shared preset fail on it
// like on an API error, so the workspace keeps the stored choice instead of reading it as "deleted" (`result: []`
// passes). A save or delete shows `error` as is — it is not an API error response
// (`@/services/api/errorToast`) — and the cache and the slice stay as they were.
class GridPresetRejectedError extends Error {}

const rejectEmpty = <TAnswer extends { result?: unknown; error?: unknown }>(answer: TAnswer): TAnswer => {
  if (answer.result) return answer;
  throw new GridPresetRejectedError(typeof answer.error === 'string' && answer.error ? answer.error : '');
};

const LIST_QUERY = { type: 'grid', version: 'be_driven_grid' } as const;

// The workspace presets list: this company's presets plus the "(all)" ones. Fetched once per company per session —
// the only writers are the save and delete mutations below, which put the server's answer into this cache.
// `cacheTime: Infinity` as well: with the default 5 minutes, a list nobody watched for that long (a document open,
// another company) is dropped and fetched again, and the menu is empty while it loads.
export const gridPresetsQuery = (companyId: string) =>
  queryOptions({
    queryKey: documentsKeys.gridPresets(companyId),
    queryFn: () => documentsApi.getGridPresets({ query: LIST_QUERY }).then(rejectEmpty),
    staleTime: Infinity,
    cacheTime: Infinity,
  });

export const useGridPresets = <TData = GridPresetsCache>(
  companyId: string,
  { select }: { select?: (cache: GridPresetsCache) => TData } = {},
) => useQuery({ ...gridPresetsQuery(companyId), select, enabled: Boolean(companyId) });

// One preset by id, for a `?sharedPreset=` link. It may belong to another user.
export const sharedGridPresetQuery = (companyId: string, presetId: string) =>
  queryOptions({
    queryKey: documentsKeys.sharedGridPreset(companyId, presetId),
    queryFn: () => documentsApi.getGridPreset({ path: { id: presetId }, query: { type: 'grid' } }).then(rejectEmpty),
    staleTime: Infinity,
    cacheTime: Infinity,
  });

export const useSharedGridPreset = (companyId: string, presetId: string | null) =>
  useQuery({ ...sharedGridPresetQuery(companyId, presetId ?? ''), enabled: Boolean(companyId && presetId) });

const onPresetError = (error: unknown) =>
  error instanceof GridPresetRejectedError && error.message ? toast.error(error.message) : showApiErrorToast(error);

const writeList = (
  qc: ReturnType<typeof useQueryClient>,
  companyId: string,
  merge: (list: NonNullable<GridPresetsCache['result']>) => NonNullable<GridPresetsCache['result']>,
) =>
  qc.setQueryData<GridPresetsCache>(
    documentsKeys.gridPresets(companyId),
    produce((draft) => {
      if (!draft) return;
      draft.result = merge(draft.result ?? []);
    }),
  );

// Other companies' cached lists hold the "(all)" presets too: mark them stale, so each is fetched again the next
// time it is shown (not now — nobody is looking at them).
const invalidateOtherCompanies = (qc: ReturnType<typeof useQueryClient>, companyId: string) =>
  qc.invalidateQueries({
    queryKey: documentsKeys.gridPresetsAll(),
    predicate: ({ queryKey }) => queryKey[2] !== companyId,
    refetchType: 'none',
  });

// Create (no `body.id`) or overwrite (with `body.id`). Same body; the method follows the id, as the legacy saga did.
export const useSaveGridPresetMutation = (companyId: string) => {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (args: SaveArgs) =>
      (args.body.id ? documentsApi.updateGridPreset(args) : documentsApi.createGridPreset(args)).then(rejectEmpty),
    onSuccess: (answer) => {
      writeList(qc, companyId, (list) => mergeSavedPreset(list, answer.result));
      invalidateOtherCompanies(qc, companyId);
    },
    onError: onPresetError,
  });
};

export const useDeleteGridPresetMutation = (companyId: string) => {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (args: DeleteArgs) => documentsApi.deleteGridPreset(args).then(rejectEmpty),
    onSuccess: (answer, { body }) => {
      writeList(qc, companyId, (list) => mergeDeletedPreset(list, answer.result, body.id));
      invalidateOtherCompanies(qc, companyId);
    },
    onError: onPresetError,
  });
};
